import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '../../../../ui/button/button.component';
import { FormFieldComponent } from '../../../../ui/form-field/form-field.component';
import { SvgIconComponent } from '../../../../ui/svg-icon/svg-icon.component';
import { CommonService } from '../../../../shared/common.service';
import { FeatureFlagService } from '../../../../core/services/feature-flag.service';
import { CustomersService } from '../../../customers/services/customers.services';
import { AddNewLocationPayload, ICustomer, ILocation } from '../../../customers/models/customers.models';
import { INewQuotation, IQuotationDetail } from '../../../quotations/models/quotations.models';
import { IPageCount } from '../../../../shared/models/page.model';
import { UiModule } from '../../../../ui/ui.module';
import { QuotesService } from '../../../quotations/services/quotations.services';
import { CreateLocationFormComponent } from '../create-location-form/create-location-form.component';

type SelectorStep = 'company' | 'location' | 'create';

/** Result emitted when the flow completes. The shape depends on the context:
 *  - `{ company, location }` — an existing company/location pair was confirmed (edit mode).
 *  - `{ orderDetail }` — a location was created inline AND attached to an order.
 *  - `IQuotationDetail` — a draft quotation was created (new-order flow). */
export type CompanyLocationResult =
  | { company: ICustomer | null; location: ILocation | null }
  | { orderDetail: any }
  | IQuotationDetail;

/**
 * Presentational 3-step company/shipping-location selector (`company -> location -> create`).
 *
 * Dialog-agnostic: driven entirely by `@Input`s and signals results through `@Output`s, so it can
 * be hosted inside a MatDialog (see {@link CompaniesSelectionDialogComponent}) or rendered inline.
 */
@Component({
  selector: 'app-company-location-selector',
  standalone: true,
  imports: [
    SharedModule,
    MatRadioModule,
    UiModule,
    ButtonComponent,
    FormFieldComponent,
    SvgIconComponent,
    CreateLocationFormComponent,
  ],
  templateUrl: './company-location-selector.component.html',
  styleUrls: ['./company-location-selector.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CompanyLocationSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  /** Pre-selected billing company (skips/short-circuits step 1 when provided). */
  @Input() company: ICustomer | null = null;
  /** Pre-selected shipping location. */
  @Input() location: ILocation | null = null;
  /** Whether the back/cancel affordances are shown. */
  @Input() canGoBack: boolean = true;
  /** `edit` confirms an existing pair; otherwise the flow drafts a new quotation. */
  @Input() mode: 'new' | 'edit' = 'new';
  /** When set, an inline-created location attaches to that order. */
  @Input() orderId?: number;
  /** Placeholder order id to fill in place ("Create manually" from a failed AI order). */
  @Input() targetId?: number | null = null;
  /** Opens the flow directly at a given step. */
  @Input() initialStep?: SelectorStep;
  /** Pre-filled address when opening directly at step "create" (name is entered manually). */
  @Input() prefillLocation?: { apartment?: string };

  /** Fired when the flow produces a result (see {@link CompanyLocationResult}). */
  @Output() completed = new EventEmitter<CompanyLocationResult>();
  /** Fired when the user backs out / cancels without a result. */
  @Output() cancelled = new EventEmitter<void>();

  companies: ICustomer[] = [];
  isLoading: boolean = false;

  step: SelectorStep = 'company';
  selectedCompany!: ICustomer | null;

  locations: ILocation[] = [];
  filteredLocations: ILocation[] = [];
  selectedLocation!: ILocation | null;

  companySearchTerm: string = '';
  locationSearchTerm: string = '';
  showGoBack: boolean = true;

  pageNumber: number = 1;
  pageSize: number = 20;

  isLoadingMore = false;
  isLoadMoreError = false;
  canLoadMore = false;

  /** Step 2 toggle: when on, the location search hits ALL locations system-wide. */
  billingShippingDifferent = false;
  /** Step 3 "Parent Company" default (the billing company chosen in step 1). */
  parentCompany: ICustomer | null = null;
  /** Internal copy of {@link prefillLocation} consumed by the create step. */
  prefill?: { apartment?: string };
  private isDirectCreate = false;
  /** When opened to edit the company, stay on the company step instead of auto-advancing. */
  private startAtCompanyStep = false;
  private preselectConsumed = false;

  // Global (cross-company) location search pagination
  globalLocations: ILocation[] = [];
  globalLocationLoading = false;
  globalLocationLoadingMore = false;
  globalLocationCanLoadMore = false;
  private globalLocationPage = 1;
  private locationSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  private scrollThreshold = 0.8;

  constructor(
    private customersService: CustomersService,
    private quotesService: QuotesService,
    private commonService: CommonService,
    private translate: TranslateService,
    public featureFlags: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.showGoBack = this.canGoBack;

    this.locationSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.globalLocationPage = 1;
        this.loadGlobalLocations(term, false);
      });

    if (this.initialStep === 'create') {
      // Open straight to the create step even when no billing company is known
      // (e.g. AI shipping address with a missing company): the form leaves the
      // company field empty and requires the user to pick one before submitting.
      this.isDirectCreate = true;
      this.parentCompany = this.company;
      this.prefill = this.prefillLocation;
      this.step = 'create';
      return;
    }

    if (this.initialStep === 'company') {
      // Edit billing company: start on the company-selection step with the current
      // company preselected; do NOT auto-advance to the location step.
      this.startAtCompanyStep = true;
      this.step = 'company';
      this.companySearchTerm = this.company?.name ?? '';
      this.loadCompanies(this.companySearchTerm);
      return;
    }

    if (this.company) {
      this.companySearchTerm = this.company.name;
      if (this.company.id) {
        this.isLoading = true;
        this.step = 'location';
        this.customersService.getCompanyById(this.company.id).subscribe({
          next: (res) => {
            const company = res.object!;
            this.companies = [company];
            this.selectedCompany = company;
            this.isLoading = false;
            this.enterLocationSelectionMode();
            this.clearPreselect();
          },
          error: () => {
            this.isLoading = false;
            this.loadCompanies(this.companySearchTerm);
          },
        });
      } else {
        this.loadCompanies(this.companySearchTerm);
        this.step = 'location';
      }
    } else {
      this.loadCompanies();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get primaryDisabled(): boolean {
    if (this.step === 'company') return !this.selectedCompany;
    if (this.step === 'location') return !this.selectedLocation;
    return false; // 'create' step validity is driven by the child form in the template
  }

  /** Locations shown in step 2: company-scoped by default, system-wide when the toggle is on. */
  get displayedLocations(): ILocation[] {
    return this.billingShippingDifferent ? this.globalLocations : this.filteredLocations;
  }

  onCompanySearch(term: string) {
    this.companySearchTerm = term;
    this.pageNumber = 1;
    this.loadCompanies(term);
  }

  onLocationSearch(term: string) {
    this.locationSearchTerm = term;
    if (this.billingShippingDifferent) {
      this.locationSearch$.next(term);
    } else {
      this.filterLocations(term);
    }
  }

  filterLocations(searchTerm?: string) {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      this.filteredLocations = this.locations.filter((location) => {
        const name = (location.name ?? '').toLowerCase();
        const apartment = (location.apartment ?? '').toLowerCase();
        const billingApartment = (location.billing_apartment ?? '').toLowerCase();
        const shippingAddress = (location.shipping_address ?? '').toLowerCase();
        const billingAddress = (location.billing_address ?? '').toLowerCase();
        return name.includes(term) || apartment.includes(term) || billingApartment.includes(term)
          || shippingAddress.includes(term) || billingAddress.includes(term);
      });
    } else {
      this.filteredLocations = this.locations;
    }
  }

  getMainLocationAddress(company: ICustomer): string {
    const mainLocation = company.locations?.find(location => location.is_main);
    if (mainLocation) {
      return mainLocation.shipping_address || mainLocation.apartment || '';
    }
    return '';
  }

  selectCompany(company: ICustomer) {
    this.selectedCompany = company;
  }

  selectLocation(location: ILocation) {
    this.selectedLocation = location;
  }

  /** Step 1/2 primary button (PROCEED on company, CONFIRM on location). */
  onPrimary(): void {
    if (this.step === 'company' && this.selectedCompany) {
      this.enterLocationSelectionMode();
      return;
    }
    if (this.step === 'location') {
      this.confirmLocation();
    }
  }

  private confirmLocation(): void {
    if (!this.selectedLocation?.id) {
      console.error('Selected location has no valid ID.');
      return;
    }

    if (this.mode === 'edit') {
      this.completed.emit({
        company: this.selectedCompany,
        location: this.selectedLocation,
      });
      return;
    }

    const payload = this.buildDraftQuotationPayload(this.selectedLocation);
    this.createDraftQuotation(payload);
  }

  /** Step 2 -> step 3. */
  goToCreate(): void {
    this.parentCompany = this.parentCompany ?? this.selectedCompany;
    this.step = 'create';
  }

  /** Emitted by the create-location form child. */
  onCreateLocation(payload: AddNewLocationPayload): void {
    this.customersService.addNewLocation(payload).subscribe({
      next: (res) => {
        if (this.orderId) {
          // Edit flow: the location was created AND attached to the order; the response is
          // the updated order detail, so let the caller merge it.
          this.completed.emit({ orderDetail: res.object });
          return;
        }

        // New-order flow: the response is the company's refreshed location list.
        const rawList = ((res?.data as ILocation[]) ?? (res?.object as any)?.locations ?? []) as ILocation[];
        const newLocation = rawList.find((l) => l.is_new);
        const list = newLocation ? [newLocation, ...rawList.filter((l) => !l.is_new)] : rawList;
        if (this.selectedCompany) {
          this.selectedCompany = { ...this.selectedCompany, locations: list };
        }
        this.locations = list;
        this.filteredLocations = list;
        this.selectedLocation = newLocation ?? list[list.length - 1] ?? null;
        this.billingShippingDifferent = false;
        this.locationSearchTerm = '';
        this.step = 'location';
      },
      error: (err) => {
        console.error('add-new-location failed:', err);
      },
    });
  }

  onBillingShippingToggle(checked: boolean): void {
    this.billingShippingDifferent = checked;
    this.selectedLocation = null;
    this.locationSearchTerm = '';
    if (checked) {
      this.globalLocationPage = 1;
      this.globalLocations = [];
      this.loadGlobalLocations('', false);
    } else if (this.selectedCompany) {
      this.loadCompanyLocations(this.selectedCompany);
    }
  }

  private enterLocationSelectionMode(): void {
    if (!this.selectedCompany) return;

    this.loadCompanyLocations(this.selectedCompany);
    this.parentCompany = this.selectedCompany;
    this.step = 'location';
    this.showGoBack = true;

    if (this.locations.length === 1) {
      this.selectedLocation = this.locations[0];
    }
    if (this.location && !this.preselectConsumed) {
      this.selectedLocation = this.locations.find(location => location.id === this.location?.id) ?? null;
    }
  }

  private buildDraftQuotationPayload(location: ILocation): any {
    return {
      partner_id: location.id!,
      // Always send the billing company alongside the shipping location.
      id_company: this.selectedCompany?.id,
      is_final: false,
      products: [],
      currency_id: location.currency?.id || 1,
      ...(location.is_new ? { is_new: true } : {}),
      ...(this.targetId ? { target_id: this.targetId } : {}),
    };
  }

  private createDraftQuotation(payload: INewQuotation): void {
    this.quotesService.draftNewQuotation(payload).subscribe({
      next: (res) => {
        this.completed.emit(res.object);
      },
      error: (err) => {
        console.error('Error creating draft order:', err);
      },
    });
  }

  back() {
    if (this.step === 'create') {
      if (this.isDirectCreate) {
        this.cancelled.emit();
        return;
      }
      this.step = 'location';
      return;
    }
    // location -> company
    this.step = 'company';
    this.selectedLocation = null;
    this.billingShippingDifferent = false;
    this.locationSearchTerm = '';
  }

  cancel() {
    this.cancelled.emit();
  }

  loadMore() {
    if (!this.canLoadMore || this.isLoadingMore) return;
    this.pageNumber++;
    this.loadCompanies(this.companySearchTerm, true);
  }

  private loadCompanies(search?: string, append = false) {
    if (!append) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
      this.isLoadMoreError = false;
    }

    const page: IPageCount = { number: this.pageNumber, size: this.pageSize };

    this.customersService.listCustomers(page, undefined, search).subscribe({
      next: (res) => {
        const newData = (res?.data as ICustomer[]) || [];
        this.canLoadMore = newData.length === this.pageSize;

        this.companies = append ? [...this.companies, ...newData] : newData;

        if (this.company && !this.preselectConsumed) {
          this.selectedCompany = this.companies.find(company => company.id === this.company?.id) ?? null;
          // When editing the company we stay on the company step (just preselect it);
          // otherwise a preselected company auto-advances to the location step.
          if (this.selectedCompany && !this.startAtCompanyStep) {
            this.enterLocationSelectionMode();
          }
          this.clearPreselect();
        }

        this.isLoading = false;
        this.isLoadingMore = false;
        this.isLoadMoreError = false;
      },
      error: (err) => {
        console.error('Error loading companies:', err);

        this.isLoading = false;
        this.isLoadingMore = false;
        this.isLoadMoreError = true;
      },
    });
  }

  /** Mark the preselect company/location as consumed so they aren't re-applied on later list loads.
   *  We flip a flag rather than nulling the `@Input`s, to keep data flow unidirectional. */
  private clearPreselect(): void {
    this.preselectConsumed = true;
  }

  private loadCompanyLocations(company: ICustomer) {
    this.locations = company.locations || [];
    this.filteredLocations = this.locations;
  }

  private loadGlobalLocations(search: string, append: boolean): void {
    if (append) this.globalLocationLoadingMore = true;
    else this.globalLocationLoading = true;

    const page: IPageCount = { number: this.globalLocationPage, size: this.pageSize };
    this.customersService.listLocations(page, undefined, search).subscribe({
      next: (res) => {
        const rows = (res?.data as ILocation[]) || [];
        this.globalLocationCanLoadMore = rows.length === this.pageSize;
        this.globalLocations = append ? [...this.globalLocations, ...rows] : rows;
        this.globalLocationLoading = false;
        this.globalLocationLoadingMore = false;
      },
      error: () => {
        this.globalLocationLoading = false;
        this.globalLocationLoadingMore = false;
      },
    });
  }

  onScroll(event: any) {
    const target = event.target;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (!this.isLoadingMore && this.canLoadMore && scrollPosition / scrollHeight > this.scrollThreshold) {
      this.pageNumber++;
      this.loadCompanies(this.companySearchTerm, true);
    }
  }

  onLocationScroll(event: any) {
    if (!this.billingShippingDifferent) return;
    const target = event.target;
    const ratio = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (!this.globalLocationLoadingMore && this.globalLocationCanLoadMore && ratio > this.scrollThreshold) {
      this.globalLocationPage++;
      this.loadGlobalLocations(this.locationSearchTerm, true);
    }
  }
}
